import './SADeleteUsersTableButton.scss'
import {buttonColors} from "../../../../Pickers/InputTheme";
import {Button} from "../../../../Form/Button/Button";
import {useContext, useState, useMemo} from "react";
import {GridReactContext} from "../../../../../Utils/DataGridWrapper/GridContext";
import {SADeleteUserConfirmationModal} from "../SADeleteUserConfirmationModal/SADeleteUserConfirmationModal";
import {I18nReactContext} from "../../../../../contexts/I18nContext";
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * Super Admin specific button for soft deleting users in bulk.
 * Opens a confirmation modal before deletion.
 * Only enabled when all selected users are active (not soft-deleted).
 * Uses SA-specific API endpoint.
 *
 * @param {object} params - The component parameters
 * @param {function} params.onSuccess - Callback function called after successful deletion
 * @returns {JSX.Element} The delete users button component
 */
export const SADeleteUsersTableButton = ({onSuccess}) => {
    const {i18n} = useContext(I18nReactContext);
    const [deleteUserConfirmationModalOpen, setDeleteUserConfirmationModalOpen] = useState(false)
    const gridContext = useContext(GridReactContext)

    // Check if all selected users are active (NOT soft-deleted)
    const allSelectedUsersAreActive = useMemo(() => {
        const selectedRows = gridContext.selectedRows || [];
        if (selectedRows.length === 0) {
            return false;
        }
        // All selected users must NOT have deletedAt field set (active users)
        return selectedRows.every(user => user.deletedAt == null);
    }, [gridContext.selectedRows]);

    const onConfirmationModalClose = () => {
        setDeleteUserConfirmationModalOpen(false)
    }
    const onClick = () => {
        setDeleteUserConfirmationModalOpen(true)
    }

    const innerOnSuccess = () => {
        setDeleteUserConfirmationModalOpen(false)
        gridContext.setSelectionModelToContext([])
        gridContext.triggerTableRefresh()
        onSuccess && onSuccess()
    }

    // Button is disabled if:
    // 1. No users selected, OR
    // 2. Not all selected users are active (at least one is already soft-deleted)
    const isButtonDisabled = !gridContext.selectionModel ||
                            gridContext.selectionModel.length === 0 ||
                            !allSelectedUsersAreActive;

    return <div>
        <Button
            data-testid="sa-delete-users-button"
            disabled={isButtonDisabled}
            color={buttonColors.GREY}
            onClick={onClick}
            icon={<DeleteIcon/>}
            text={i18n.translate('allUsersTable.buttons.deleteUsers')}
        />
        {deleteUserConfirmationModalOpen && (
            <SADeleteUserConfirmationModal
                deleteUserConfirmationModalOpen={deleteUserConfirmationModalOpen}
                onCloseDeleteUserConfirmationModal={onConfirmationModalClose}
                onSuccess={innerOnSuccess}
            />
        )}
    </div>
}
